import React, { useEffect, useState } from 'react';
import Bread from 'modules/layoutModule/components/Bread/Bread';
import { HomeOutlined, EditOutlined, DeleteFilled } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  Breadcrumb,
  Pagination,
  Popconfirm,
  ConfigProvider,
  BackTop,
} from 'antd';
import { Table, Space, Tooltip, Button } from 'antd';
import TableDateRow from 'modules/layoutModule/components/TableDateRow/TableDateRow';
import {
  getFieldNewsRequest,
  removeSpecificFieldNewsRequest,
} from 'modules/fieldNewsModule/requests/fieldNewsRequests';
import plPL from 'antd/lib/locale/pl_PL';
import moment from 'moment';
import TableDataRowTypes from 'modules/layoutModule/enums/TableDataRowTypes';
import useColumnSearch from 'modules/layoutModule/hooks/useColumnSearch';
import fieldNewsTypes from '../../../helpers/fieldNewsTypes';

export interface NewsTableTypes {
  id: number;
  dateAdd: moment.MomentInput;
  name: string;
  isActive: number;
}

const FieldNews = (): JSX.Element => {
  const [tableData, setTableData] = useState<NewsTableTypes[]>([]);
  const [loading, toggleLoading] = useState<boolean>(true);
  const [searchedColumn, setSearchedColumn] = useState('');

  const confirmDelete = (id: number) => {
    removeSpecificFieldNewsRequest(id).then(() => {
      downloadNews();
    });
  };

  const downloadNews = () =>
    getFieldNewsRequest().then((response) => {
      if (response) {
        const { data } = response;
        setTableData(data);
        toggleLoading(false);
      } else {
        toggleLoading(false);
      }
    });

  useEffect(() => {
    if (tableData.length === 0) {
      downloadNews();
    }
  }, [tableData]);

  const columns = [
    {
      title: 'Data',
      dataIndex: 'dateAdd',
      key: 'dateAdd',
      width: 70,
      render: (
        text: string,
        record: {
          type: number;
          dateAdd: Date;
          id: number;
          isActive: number | boolean;
          taste: number;
          colour: number;
          category?: string | undefined;
          url: string | undefined;
        }
      ) => <TableDateRow record={record} field={TableDataRowTypes.DATE_ADD} />,
      sorter: (
        a: { dateAdd: moment.MomentInput },
        b: { dateAdd: moment.MomentInput }
      ) => moment(a.dateAdd).unix() - moment(b.dateAdd).unix(),
    },
    {
      title: 'Typ',
      dataIndex: 'contentType',
      key: 'contentType',
      width: 100,
      filters: fieldNewsTypes.map((type) => ({
        text: type.label,
        value: type.value,
      })),
      onFilter: (value: string, record: any) => record.contentType === value,
      render: (text: string, record: any) => (
        <TableDateRow
          record={record}
          field={TableDataRowTypes.FIELD_NEWS_TYPE}
        />
      ),
      sorter: (a: { contentType: number }, b: { contentType: number }) =>
        a.contentType - b.contentType,
    },
    {
      title: 'Tytuł',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: { name: string }, b: { name: string }) =>
        a.name.localeCompare(b.name),
      ...useColumnSearch(
        'name',
        'Szukaj po tytule',
        'Szukaj',
        'Resetuj',
        searchedColumn,
        setSearchedColumn
      ),
      render: (text: string, record: any) => (
        <TableDateRow
          record={record}
          field={TableDataRowTypes.NAME_LINK}
          link={`aktualnosci/wiadomosci-terenowe/${record.categoryUrl}/${record.sectionUrl}/${record.url}`}
        />
      ),
    },
    {
      title: 'Okładka',
      dataIndex: 'image',
      key: 'image',
      width: 100,
      render: (
        text: string,
        record: {
          type: number;
          dateAdd: Date;
          id: number;
          isActive: number | boolean;
          taste: number;
          colour: number;
          category?: string | undefined;
          url: string | undefined;
        }
      ) => <TableDateRow record={record} field={TableDataRowTypes.IMAGE} />,
    },
    {
      title: 'Kategoria',
      dataIndex: 'idCategory',
      width: 160,
      sorter: (a: { idCategory: number }, b: { idCategory: number }) =>
        a.idCategory - b.idCategory,
      render: (text: string, record: any) => (
        <TableDateRow
          record={record}
          field={TableDataRowTypes.FIELD_NEWS_CATEGORY}
        />
      ),
    },
    {
      title: 'Regiony',
      dataIndex: 'idSection',
      width: 160,
      sorter: (a: { idSection: number }, b: { idSection: number }) =>
        a.idSection - b.idSection,
      render: (text: string, record: any) => (
        <TableDateRow
          record={record}
          field={TableDataRowTypes.FIELD_NEWS_SECTIONS}
        />
      ),
    },
    {
      title: 'Licznik',
      dataIndex: 'viewCount',
      width: 100,
      sorter: (a: { viewCount: number }, b: { viewCount: number }) =>
        a.viewCount - b.viewCount,
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.VIEWS} />
      ),
    },
    {
      title: 'Aktywna',
      dataIndex: 'isActive',
      key: 'isActive',
      width: '150px',
      render: (text: string, record: any) => (
        <TableDateRow record={record} field={TableDataRowTypes.IS_ACTIVE} />
      ),
      sorter: (a: { isActive: number }, b: { isActive: number }) =>
        a.isActive - b.isActive,
      filters: [
        {
          text: 'TAK',
          value: 'isActive',
        },
        {
          text: 'NIE',
          value: 'isNotActive',
        },
      ],
      onFilter: (value: string, record: { isActive: boolean }) => {
        if (value === 'isActive') {
          return record.isActive;
        }
        return !record.isActive;
      },
    },
    {
      title: 'Akcje',
      dataIndex: 'operation',
      key: 'operation',
      width: 100,
      render: (text: string, item: NewsTableTypes) => (
        <Space
          size="middle"
          style={{ display: 'Flex', flexDirection: 'column' }}
        >
          <Tooltip title="Edytuj wpis">
            <Link to={`/field-news/${item.id}`}>
              <Button type="primary" shape="circle" icon={<EditOutlined />} />
            </Link>
          </Tooltip>
          <Tooltip title="Usuń wpis">
            <Popconfirm
              title="Czy na pewno chcesz usunąc ten wpis?"
              placement="left"
              onConfirm={() => confirmDelete(item.id)}
              okText="Tak"
              cancelText="Nie"
            >
              <Button type="link" disabled={item.isActive === 1}>
                <DeleteFilled />
              </Button>
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Bread>
        <>
          <Breadcrumb.Item>
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Aktualności</Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/field-news">Lista wiadomości terenowych</Link>
          </Breadcrumb.Item>
        </>
      </Bread>
      <ConfigProvider locale={plPL}>
        <Table
          // @ts-ignore
          columns={columns}
          // @ts-ignore
          dataSource={tableData}
          loading={loading}
          rowKey={(record) => record.id}
        >
          <Pagination />
        </Table>
        <BackTop />
      </ConfigProvider>
    </>
  );
};

export default FieldNews;
